import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Send, Bot, User, Loader2, CheckCircle2, X, Edit3, RotateCcw, AlertTriangle, Sparkles, Mic, Upload, ExternalLink } from 'lucide-react';

/**
 * Operator Mira chat panel — Week 1 (text chat, daily_sales only).
 *
 * Strictly follows the six-beat interaction flow from
 * /app/memory/OPERATOR_MIRA_DOCTRINE.md v1.0:
 *   Beat 1 — user types command
 *   Beat 2 — POST /parse → structured intent or clarifying question
 *   Beat 3 — Preview card rendered
 *   Beat 4 — user clicks Confirm / Modify / Cancel
 *   Beat 5 — POST /execute → receipt
 *   Beat 6 — receipt shown with Undo button (24h window)
 */
export default function OperatorMiraPanel({ initialPrefill = '', onPrefillConsumed }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  // Quick vs Deep Analysis toggle (doctrine v2.0). Quick = fast answers,
  // Deep = step-by-step Claude reasoning + 4k max_tokens + strategic bias.
  const [deepMode, setDeepMode] = useState(false);
  // One session_id per chat load → Mira remembers the last 50 turns
  // within a session. New page load = fresh session. Follow-ups like
  // "what is missing?" resolve against the prior turn.
  const sessionIdRef = useRef(`mira-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [dragOver, setDragOver] = useState(false);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const invoiceInputRef = useRef(null);
  // Reference to the chat input so a deep-link prefill can auto-scroll
  // it into view (otherwise the input is buried below 4+ admin tiles).
  const inputRef = useRef(null);
  // Track whether we have already consumed the deep-link `prefill` so a
  // re-mount (StrictMode dev double-fire, route remounts) cannot keep
  // overwriting whatever the user is typing.
  const prefillConsumedRef = useRef(false);

  // One-shot deep-link prefill — when AdminPanel passes ?prefill=… from
  // the TDB Missing-Days Ticker, drop the command into the input so the
  // leader only has to replace the 0s and hit Send.
  useEffect(() => {
    if (initialPrefill && !prefillConsumedRef.current) {
      setInput(initialPrefill);
      prefillConsumedRef.current = true;
      onPrefillConsumed?.();
      toast.info('Prefilled from TDB ticker — edit numbers and press Send', {
        description: initialPrefill,
      });
      // Auto-scroll the chat input into view and focus it so the leader
      // can immediately start editing the 0 placeholders.
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          inputRef.current.focus();
        }
      }, 200);
    }
  }, [initialPrefill, onPrefillConsumed]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const today = new Date().toISOString().slice(0, 10);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const r = await api.post('/operator-mira/parse', { command: text, today, session_id: sessionIdRef.current, deep: deepMode });
      const parsed = r.data.parsed;
      // Marketing intents are read-only — execute immediately and render the
      // result inline. No "Show me" confirm step needed.
      if (typeof parsed.intent === 'string' && parsed.intent.startsWith('marketing_')) {
        try {
          const ex = await api.post('/operator-mira/execute', {
            ...parsed,
            raw_command: text,
            llm_model: r.data.llm_model,
            llm_tokens: r.data.tokens,
          });
          setMessages((m) => [...m, {
            role: 'mira', kind: 'marketing_result',
            intent: parsed.intent,
            humanSummary: parsed.human_summary || ex.data.summary,
            data: ex.data,
            rawCommand: text,
          }]);
        } catch (e) {
          const msg = e?.response?.data?.detail || 'Could not fetch marketing data.';
          setMessages((m) => [...m, { role: 'mira', kind: 'error', text: msg }]);
        }
        return;
      }
      setMessages((m) => [...m, {
        role: 'mira',
        kind: parsed.intent,
        parsed,
        tokens: r.data.tokens,
        rawCommand: text,
        llmModel: r.data.llm_model,
        env: r.data.env,
        envWarning: r.data.env_warning,
      }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Something went wrong.';
      setMessages((m) => [...m, { role: 'mira', kind: 'error', text: msg }]);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (msg) => {
    setBusy(true);
    const p = msg.parsed || {};
    try {
      // Forward EVERY parsed field — each intent executor needs its own
      // fields (wo_title, asset_name, recipe_name, mn_summary, etc.).
      // Cherry-picking broke 12 of 13 intents silently.
      const payload = {
        ...p,
        raw_command: msg.rawCommand,
        llm_model: msg.llmModel,
        llm_tokens: msg.tokens,
      };
      const r = await api.post('/operator-mira/execute', payload);
      const receipt = r.data.receipt || {};
      // File generators return a `file` object — surface it in the receipt
      // so the user gets a download button directly in chat.
      const filePayload = r.data.file || r.data.draft || null;
      // Research results and analyses — extract the body the user will read
      const analysis = r.data.analysis;
      const research = r.data.query ? r.data : null;
      setMessages((m) => [...m, {
        role: 'mira',
        kind: 'receipt',
        receipt,
        entityType: r.data.entity_type,
        entityId: r.data.entity_id,
        auditId: r.data.audit_id,
        action: r.data.action,
        undoExpiresAt: r.data.undo_expires_at,
        parsed: p,
        file: filePayload && filePayload.download_url ? filePayload : null,
        draft: r.data.draft || null,
        analysis,
        research,
      }]);
      toast.success(receipt.human_summary || 'Saved ✓', {
        description: '24 h undo window available.',
        duration: 5000,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Save failed.';
      toast.error(`Save failed: ${detail}`);
      setMessages((m) => [...m, { role: 'mira', kind: 'error', text: detail }]);
    } finally {
      setBusy(false);
    }
  };

  const executeRead = async (msg) => {
    setBusy(true);
    const p = msg.parsed || {};
    try {
      const r = await api.post('/operator-mira/execute', {
        ...p,
        intent: p.intent || 'read_generic',
        raw_command: msg.rawCommand,
        llm_model: msg.llmModel,
        llm_tokens: msg.tokens,
      });
      // reconcile_data returns a specialised report shape — render the
      // match/mismatch/missing breakdown, with an "Add missing rows" CTA.
      if (p.intent === 'reconcile_data' || r.data.matches !== undefined) {
        setMessages((m) => [...m, {
          role: 'mira', kind: 'reconcile_report',
          matches: r.data.matches || [],
          mismatches: r.data.mismatches || [],
          missing: r.data.missing || [],
          collection: r.data.collection || p.recon_collection,
          divisionId: r.data.division_id || p.recon_division_id,
          keyFields: r.data.key_fields || [],
          totalMissing: r.data.total_missing || 0,
          humanSummary: r.data.summary,
          rawCommand: msg.rawCommand,
        }]);
        return;
      }
      // The generic read returns { rows, total, count, target } in the body
      // (daily_sales flow returns rows at top level too). Normalise here.
      // Bundle 2.8 — Outlet Intelligence intents (top dishes / kitchen mix /
      // food cost / stays pulse / etc.) ship a conversational `chat_summary`
      // multi-paragraph block in the response. When present, render it as
      // narrative prose (preferred over the row-style fallback).
      const chatSummary = r.data.chat_summary;
      const waSummary = r.data.summary;
      if (chatSummary) {
        setMessages((m) => [...m, {
          role: 'mira', kind: 'analysis_result',
          analysis: chatSummary,
          waSummary,
          humanSummary: r.data.receipt?.human_summary || p.human_summary,
        }]);
        return;
      }
      const rows = r.data.rows || [];
      const total = r.data.total || 0;
      const target = r.data.target || 'daily_sales';
      setMessages((m) => [...m, {
        role: 'mira', kind: 'read_result', rows, total, target,
        humanSummary: r.data.receipt?.human_summary || p.human_summary,
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Read failed.');
    } finally {
      setBusy(false);
    }
  };

  const undo = async (msg) => {
    try {
      const r = await api.post(`/operator-mira/undo/${msg.auditId}`);
      toast.success(r.data.detail);
      setMessages((m) => [...m, { role: 'mira', kind: 'undone', detail: r.data.detail }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Undo failed.');
    }
  };

  const sendPill = (text) => { setInput(text); setTimeout(send, 50); };

  // ---- Voice: tap-and-hold mic → Whisper transcription ----
  const startRecording = async () => {
    if (busy || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 500) { setRecording(false); return; }  // too short — likely tap misfire
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'mira.webm');
          const r = await api.post('/operator-mira/voice/transcribe', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const text = (r.data?.text || '').trim();
          if (!text) { toast.error('Could not hear anything. Try again.'); return; }
          setInput(text);
          setTimeout(send, 50);
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Voice transcription failed.');
        } finally {
          setBusy(false);
          setRecording(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error('Microphone access denied.');
    }
  };
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  // ---- File upload: CSV, XLSX, PDF, images, DOCX ---------------------------
  // Routes to /api/operator-mira/upload which returns a ready-to-preview
  // intent (reconcile_data / bulk_import / add_cost_entry / add_sop).
  const onFilePicked = async (fileOrEvent) => {
    const f = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (fileOrEvent?.target) fileOrEvent.target.value = '';
    if (!f) return;
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', text: `📎 ${f.name} (${Math.round(f.size / 1024)} KB)` }]);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('session_id', sessionIdRef.current);
      if (input.trim()) fd.append('command', input.trim());
      const r = await api.post('/operator-mira/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parsed = r.data?.parsed;
      if (!parsed) {
        toast.error("Couldn't understand that file.");
        return;
      }
      setInput('');
      setMessages((m) => [...m, {
        role: 'mira',
        kind: parsed.intent,
        parsed,
        rawCommand: `[file] ${f.name}`,
        llmModel: 'gemini:gemini-2.5-flash',
        fileInfo: r.data.file_info,
      }]);
      toast.success(parsed.human_summary || 'File parsed — review the preview.');
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'File upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFilePicked(f);
  };

  return (
    <div className="flex flex-col h-full" data-testid="operator-mira-panel">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAF8F5' }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1B2A4A' }}>
          <Sparkles size={14} style={{ color: '#F5C86B' }} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Operator Mira</p>
          <p className="text-[10.5px]" style={{ color: '#6B7280' }}>Sales · Costs · Recipes · Assets · People · Pipeline · Receivables · Vendors · SOPs · Compliance · Work orders · Notes. Every action needs your Confirm.</p>
        </div>
        <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold" style={{ backgroundColor: '#1B2A4A', color: '#F5C86B' }}>v1.2</span>
      </div>

      {/* Messages */}
      <div ref={scrollRef}
        onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className="flex-1 overflow-y-auto p-3 space-y-2 relative"
        style={{ backgroundColor: '#FDFCFA', minHeight: 400 }}
        data-testid="mira-message-area">
        {dragOver && (
          <div className="absolute inset-2 rounded-lg border-2 border-dashed flex items-center justify-center z-10 pointer-events-none"
            style={{ borderColor: '#2A7B88', backgroundColor: 'rgba(42,123,136,0.08)' }}>
            <span className="text-sm font-semibold" style={{ color: '#2A7B88' }}>
              Drop to upload · CSV, Excel, PDF, image, or Word
            </span>
          </div>
        )}
        {messages.length === 0 && (
          <div className="text-center py-8" style={{ color: '#9CA3AF' }}>
            <Bot size={36} className="mx-auto mb-2 opacity-50" />
            <p className="text-xs mb-3">Try any of these — or type anything</p>
            <div className="flex flex-col gap-1.5 max-w-md mx-auto">
              {[
                'Add ₹25012 to TDB Bengaluru for today',
                'Add ₹4500 fuel expense for TDB Mumbai today',
                'Add recipe: Chicken Cake, 500g chicken ₹150, 200g flour ₹30, sell ₹550',
                'Add a Bosch dishwasher to Stays kitchen, ₹45000, today',
                "Update Srikanth's phone to 9886000988",
                'Add pipeline deal: Infosys, ₹2Cr, LC, stage Qualified',
                'Book April billing of ₹13.33L for RedBeryl',
                'Create urgent work order: AC broken in Suite 5 at Stays',
                'Update FSSAI expiry for Celeste Ketti to 31-Dec-2027',
              ].map((ex) => (
                <button key={ex} onClick={() => sendPill(ex)}
                  className="text-left text-[11.5px] px-3 py-1.5 rounded-md border hover:bg-[#F7F5F2] transition"
                  style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                  data-testid="mira-example-pill">
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => <Message key={i} msg={m} onConfirm={confirm} onCancel={() => setMessages((ms) => ms.filter((_, j) => j !== i))}
          onModify={() => setInput(m.rawCommand || '')} onUndo={undo} onRead={executeRead} onPill={sendPill} />)}

        {busy && (
          <div className="flex items-center gap-2 px-3 py-2 text-xs" style={{ color: '#6B7280' }}>
            <Loader2 size={12} className="animate-spin" />
            Mira is thinking...
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t p-2 flex gap-2 items-end" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAF8F5' }}>
        <input
          ref={invoiceInputRef}
          type="file"
          accept=".csv,.xlsx,.pdf,.docx,.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: 'none' }}
          onChange={onFilePicked}
          data-testid="mira-file-input"
        />
        <button
          onClick={() => invoiceInputRef.current?.click()}
          disabled={busy}
          title="Attach file (CSV, Excel, PDF, image, Word)"
          className="px-2 py-2 rounded-md border transition hover:bg-white disabled:opacity-50"
          style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
          data-testid="mira-file-btn"
        >
          <Upload size={14} />
        </button>
        <button
          onMouseDown={startRecording}
          onMouseUp={stopRecording}
          onMouseLeave={recording ? stopRecording : undefined}
          onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
          onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
          disabled={busy && !recording}
          title="Hold to speak"
          className="px-2 py-2 rounded-md border transition disabled:opacity-50"
          style={{
            borderColor: recording ? '#B91C1C' : '#E5E1DC',
            backgroundColor: recording ? '#FEF2F2' : 'transparent',
            color: recording ? '#B91C1C' : '#2A7B88',
          }}
          data-testid="mira-voice-btn"
        >
          <Mic size={14} className={recording ? 'animate-pulse' : ''} />
        </button>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={recording ? "🎙 Listening… release to send" : "Say anything — 'Add 25012 to TDB Bengaluru today'"}
          rows={1}
          className="flex-1 px-3 py-2 rounded-md border text-sm resize-none outline-none focus:border-[#2A7B88]"
          style={{ borderColor: '#E5E1DC', maxHeight: 80 }}
          disabled={busy}
          data-testid="mira-input"
        />
        <button onClick={send} disabled={busy || !input.trim()}
          className="px-3 py-2 rounded-md text-white transition disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
          style={{ backgroundColor: '#2A7B88' }}
          data-testid="mira-send-btn">
          <Send size={14} />
        </button>
      </div>
      {/* Quick vs Deep toggle — doctrine v2.0.
          Quick: fast answers for daily data entry.
          Deep:  Claude thinks step-by-step, returns 3-5 paragraph analysis. */}
      <div className="flex items-center gap-2 mt-2 text-[11px]" data-testid="mira-mode-toggle">
        <span style={{ color: '#6B7280' }}>Mode:</span>
        <button
          onClick={() => setDeepMode(false)}
          className="px-2.5 py-1 rounded-full transition-colors"
          style={{
            backgroundColor: !deepMode ? '#2A7B88' : 'transparent',
            color: !deepMode ? 'white' : '#6B7280',
            border: `1px solid ${!deepMode ? '#2A7B88' : '#E5E1DC'}`,
            fontWeight: !deepMode ? 600 : 400,
          }}
          data-testid="mira-mode-quick">
          ⚡ Quick
        </button>
        <button
          onClick={() => setDeepMode(true)}
          className="px-2.5 py-1 rounded-full transition-colors"
          style={{
            backgroundColor: deepMode ? '#1B2A4A' : 'transparent',
            color: deepMode ? 'white' : '#6B7280',
            border: `1px solid ${deepMode ? '#1B2A4A' : '#E5E1DC'}`,
            fontWeight: deepMode ? 600 : 400,
          }}
          data-testid="mira-mode-deep">
          🧠 Deep
        </button>
        <span className="ml-1" style={{ color: '#9CA3AF' }}>
          {deepMode ? '10–20s · 3–5 paragraph analysis' : '~2s · fast answer'}
        </span>
      </div>
    </div>
  );
}

function Message({ msg, onConfirm, onCancel, onModify, onUndo, onRead, onPill }) {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[75%] px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: '#2A7B88', color: 'white' }}>{msg.text}</div>
      </div>
    );
  }
  if (msg.kind === 'error') {
    return (
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} style={{ color: '#DC2626' }} className="flex-shrink-0 mt-1" />
        <div className="px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#991B1B', borderLeft: '3px solid #DC2626' }}>{msg.text}</div>
      </div>
    );
  }
  if (msg.kind === 'needs_clarification') {
    return (
      <div className="flex items-start gap-2">
        <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
        <div className="flex-1">
          <div className="px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: '#FEF3C7', color: '#78350F', borderLeft: '3px solid #F59E0B' }}>
            {msg.parsed.clarifying_question || msg.parsed.human_summary}
          </div>
          {Array.isArray(msg.parsed.pills) && msg.parsed.pills.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {msg.parsed.pills.map((p) => (
                <button key={p} onClick={() => onPill(p)}
                  className="text-[11px] px-2.5 py-1 rounded-full border hover:bg-[#F7F5F2]"
                  style={{ borderColor: '#F59E0B', color: '#78350F' }}>{p}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (msg.kind === 'unsupported') {
    return (
      <div className="flex items-start gap-2">
        <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
        <div className="px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: '#F3F0EB', color: '#1B2A4A' }}>
          {msg.parsed.human_summary || "That's not in my current capability list."}
        </div>
      </div>
    );
  }
  if (msg.kind === 'read_daily_sales' || msg.kind === 'read_generic') {
    // Graceful text rendering — when the LLM slipped into prose (JSON parse
    // fallback) or just answered a question conversationally, show it as a
    // chat bubble. Confirm buttons would be confusing here.
    const isTextOnly = msg.parsed?._non_json_fallback
      || (msg.parsed?.read_target === 'note')
      || (!msg.parsed?.read_target && !msg.parsed?.division_id && !msg.parsed?.outlet);
    if (isTextOnly) {
      return (
        <div className="flex items-start gap-2" data-testid="mira-text-reply">
          <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
          <div className="flex-1 px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'white', color: '#1B2A4A', border: '1px solid #E5E1DC' }}>
            {msg.parsed?.human_summary || '—'}
          </div>
        </div>
      );
    }
    return (
      <PreviewCard msg={msg} onConfirm={() => onRead(msg)} confirmLabel="Show me"
        onCancel={onCancel} onModify={onModify} readOnly />
    );
  }
  if (msg.kind === 'reconcile_data' || msg.kind === 'bulk_import') {
    return (
      <PreviewCard msg={msg}
        onConfirm={() => (msg.kind === 'reconcile_data' ? onRead(msg) : onConfirm(msg))}
        confirmLabel={msg.kind === 'reconcile_data' ? 'Reconcile against DB' : 'Import all rows'}
        onCancel={onCancel} onModify={onModify} />
    );
  }
  if (msg.kind === 'reconcile_report') {
    return <ReconcileReport msg={msg} />;
  }
  if (msg.kind === 'marketing_result') {
    return <MarketingResult msg={msg} />;
  }
  if (msg.kind === 'add_daily_sales' || msg.kind === 'update_daily_sales' ||
      msg.kind === 'add_cost_entry' || msg.kind === 'add_bom_recipe' ||
      msg.kind === 'add_asset' || msg.kind === 'update_person' ||
      msg.kind === 'add_pipeline_deal' || msg.kind === 'mark_payment_received' ||
      msg.kind === 'add_b2b_revenue' || msg.kind === 'add_vendor' ||
      msg.kind === 'add_sop' || msg.kind === 'update_compliance' ||
      msg.kind === 'add_work_order' || msg.kind === 'add_meeting_note') {
    return (
      <PreviewCard msg={msg} onConfirm={() => onConfirm(msg)} confirmLabel="Confirm & save"
        onCancel={onCancel} onModify={onModify} />
    );
  }
  // Power intents — research, analysis, content generation. Preview card
  // confirms before running (these calls are slow + expensive). Result comes
  // back as a receipt with research/analysis/file payloads embedded.
  if (msg.kind === 'web_research' || msg.kind === 'strategic_analysis' ||
      msg.kind === 'generate_report' || msg.kind === 'generate_excel' ||
      msg.kind === 'generate_presentation' || msg.kind === 'draft_message') {
    const labels = {
      web_research: 'Run web research',
      strategic_analysis: 'Run analysis',
      generate_report: 'Generate report',
      generate_excel: 'Generate spreadsheet',
      generate_presentation: 'Generate deck',
      draft_message: 'Draft message',
    };
    return (
      <PreviewCard msg={msg} onConfirm={() => onConfirm(msg)}
        confirmLabel={labels[msg.kind] || 'Run'}
        onCancel={onCancel} onModify={onModify} />
    );
  }
  if (msg.kind === 'receipt') {
    return <Receipt msg={msg} onUndo={onUndo} />;
  }
  if (msg.kind === 'analysis_result') {
    // Bundle 2.8 — conversational rendering for Outlet Intelligence
    // intents. Preferred over read_result when the executor ships a
    // `chat_summary` multi-paragraph block.
    const text = msg.analysis || msg.waSummary || '';
    const paragraphs = text.split(/\n\n+/).filter(Boolean);
    return (
      <div className="flex items-start gap-2" data-testid="mira-analysis-result">
        <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
        <div className="flex-1 rounded-lg border p-3 text-sm leading-relaxed"
             style={{ borderColor: '#E5E1DC', backgroundColor: 'white', color: '#1B2A4A' }}>
          {paragraphs.map((para, i) => {
            // Render markdown bold (**word**) inline
            const parts = para.split(/(\*\*[^*]+\*\*)/g);
            return (
              <p key={i} className={i < paragraphs.length - 1 ? 'mb-3' : ''}>
                {parts.map((part, j) =>
                  part.startsWith('**') && part.endsWith('**') ? (
                    <strong key={j} style={{ color: '#1B2A4A' }}>
                      {part.slice(2, -2)}
                    </strong>
                  ) : (
                    <span key={j}>{part}</span>
                  )
                )}
              </p>
            );
          })}
        </div>
      </div>
    );
  }
  if (msg.kind === 'read_result') {
    const rows = msg.rows || [];
    const total = msg.total || 0;
    const target = msg.target || 'daily_sales';
    const formatRow = (r, i) => {
      // Pick the best 2-3 fields per row based on target
      let left, right;
      if (target === 'b2b_revenue') {
        left = `${r.client_name || '—'} · ${r.month || ''}`;
        right = `₹${Number(r.amount || 0).toLocaleString('en-IN')}`;
      } else if (target === 'receivables') {
        left = `${r.client || '—'} · ${r.division || ''} · ${r.expected_date || ''}`;
        right = `₹${Number(r.outstanding || 0).toLocaleString('en-IN')}`;
      } else if (target === 'pipeline_deals') {
        left = `${r.client_company || r.deal_name || '—'} · ${r.stage || ''}`;
        right = r.deal_value ? `₹${Number(r.deal_value).toLocaleString('en-IN')}` : '';
      } else if (target === 'contracts') {
        left = `${r.client_name || r.client || '—'} · ${r.payment_structure || ''}`;
        right = '';
      } else if (target === 'meeting_notes') {
        left = `${r.date || ''} · ${r.client_name || '—'}`;
        right = r.summary ? (r.summary.length > 60 ? r.summary.slice(0, 60) + '…' : r.summary) : '';
      } else {
        left = `${r.date || ''} · ${r.outlet || ''}`;
        right = `₹${Number(r.amount || 0).toLocaleString('en-IN')}`;
      }
      return (
        <div key={i} className="flex justify-between gap-3 py-1 border-b last:border-0" style={{ borderColor: '#F3F0EB' }}>
          <span className="truncate">{left}</span>
          <span className="font-mono flex-shrink-0">{right}</span>
        </div>
      );
    };
    return (
      <div className="flex items-start gap-2" data-testid="mira-read-result">
        <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
        <div className="flex-1 rounded-lg border p-3 text-xs" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
          <p className="font-semibold mb-2" style={{ color: '#1B2A4A' }}>
            {msg.humanSummary || `${target.replace('_', ' ')} · ${rows.length} row${rows.length === 1 ? '' : 's'}`}
            {total ? ` · ₹${Number(total).toLocaleString('en-IN')}` : ''}
          </p>
          {rows.length > 0 ? (
            <div className="space-y-1 max-h-60 overflow-y-auto">{rows.map(formatRow)}</div>
          ) : (
            <p className="italic" style={{ color: '#9CA3AF' }}>No records found.</p>
          )}
        </div>
      </div>
    );
  }
  if (msg.kind === 'undone') {
    return (
      <div className="flex items-start gap-2">
        <RotateCcw size={14} style={{ color: '#6B7280' }} className="flex-shrink-0 mt-1" />
        <div className="px-3 py-2 rounded-lg text-sm italic" style={{ backgroundColor: '#F3F0EB', color: '#6B7280' }}>{msg.detail}</div>
      </div>
    );
  }
  return null;
}

function PreviewCard({ msg, onConfirm, onCancel, onModify, confirmLabel, readOnly }) {
  const p = msg.parsed;
  // Curated field order — these keys render as bold "Key: value" rows if present.
  // Anything else just falls through silently to keep the card clean.
  const displayFields = [
    ['Division', p.division_id?.toUpperCase?.()],
    ['Outlet', p.outlet || p.asset_location || p.wo_outlet || p.compliance_outlet],
    ['Date', p.date || p.asset_purchase_date],
    ['Amount', p.amount ? `₹${Number(p.amount).toLocaleString('en-IN')}` : null],
    ['Cost', p.cost_amount ? `₹${Number(p.cost_amount).toLocaleString('en-IN')} (${p.cost_category || 'misc'})` : null],
    ['Recipe', p.recipe_name],
    ['Sell price', p.sell_price ? `₹${Number(p.sell_price).toLocaleString('en-IN')}` : null],
    ['Asset', p.asset_name ? `${p.asset_name}${p.asset_cost ? ` · ₹${Number(p.asset_cost).toLocaleString('en-IN')}` : ''}` : null],
    ['Person', p.person_name],
    ['Updates', p.updates ? Object.entries(p.updates).map(([k, v]) => `${k}=${v}`).join(', ') : null],
    ['Deal', p.deal_name || p.client_company],
    ['Deal value', p.deal_value ? `₹${Number(p.deal_value).toLocaleString('en-IN')}` : null],
    ['Stage', p.stage],
    ['Owner', p.owner],
    ['Invoice', p.invoice_identifier],
    ['Received', p.received_amount ? `₹${Number(p.received_amount).toLocaleString('en-IN')}` : null],
    ['Client', p.client_name || p.mn_client],
    ['Month', p.month],
    ['Vendor', p.vendor_name],
    ['Net terms', p.payment_terms_days ? `${p.payment_terms_days} days` : null],
    ['SOP', p.sop_title],
    ['Target outlets', Array.isArray(p.sop_target_outlets) ? p.sop_target_outlets.join(', ') : null],
    ['Compliance', p.compliance_item],
    ['New expiry', p.new_expiry_date],
    ['Work order', p.wo_title],
    ['Priority', p.wo_priority],
    ['Attendees', Array.isArray(p.mn_attendees) ? p.mn_attendees.join(', ') : null],
    ['Summary', p.mn_summary],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  const subs = p.sub_amounts || {};
  const subKeys = Object.keys(subs);
  const impact = p.impact || {};
  const ingredients = p.ingredients || [];

  return (
    <div className="flex items-start gap-2" data-testid="mira-preview-card">
      <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
      <div className="flex-1 rounded-lg border p-3" style={{ borderColor: '#2A7B88', backgroundColor: 'white', borderLeftWidth: 3 }}>
        {msg.envWarning && (
          <div
            className="mb-2 rounded border px-2 py-1.5 text-[11px] font-medium"
            style={{ borderColor: '#FBBF24', backgroundColor: '#FFFBEB', color: '#92400E' }}
            data-testid="mira-env-warning"
          >
            {msg.envWarning}
          </div>
        )}
        <p className="text-[11px] uppercase tracking-wide font-medium mb-2" style={{ color: '#2A7B88' }}>About to do · {p.intent?.replace('_', ' ')}</p>
        <p className="text-sm font-semibold mb-2" style={{ color: '#1B2A4A' }}>{p.human_summary || p.intent}</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] mb-2">
          {displayFields.map(([k, v]) => <Row key={k} label={k} value={v} highlight={k === 'Amount' || k === 'Cost' || k === 'Deal value'} />)}
        </div>
        {subKeys.length > 0 && (
          <div className="mt-2 pt-2 border-t" style={{ borderColor: '#F3F0EB' }}>
            <p className="text-[11px] uppercase tracking-wide font-medium mb-1" style={{ color: '#6B7280' }}>Breakdown</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11.5px]">
              {subKeys.map((k) => <Row key={k} label={k} value={`₹${Number(subs[k] || 0).toLocaleString('en-IN')}`} />)}
            </div>
          </div>
        )}
        {ingredients.length > 0 && (
          <div className="mt-2 pt-2 border-t" style={{ borderColor: '#F3F0EB' }}>
            <p className="text-[11px] uppercase tracking-wide font-medium mb-1" style={{ color: '#6B7280' }}>Ingredients</p>
            {ingredients.map((i, idx) => (
              <div key={idx} className="flex justify-between text-[11.5px] py-0.5">
                <span>{i.qty} {i.name}</span>
                <span className="font-mono">₹{Number(i.cost || 0).toLocaleString('en-IN')}</span>
              </div>
            ))}
            <div className="flex justify-between text-[11.5px] mt-1 pt-1 border-t" style={{ borderColor: '#F3F0EB' }}>
              <span>Labour</span>
              <span className="font-mono">₹{Number(p.labour_cost || 0).toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}
        {impact.is_replace && (
          <div className="mt-2 p-2 rounded text-[11px]" style={{ backgroundColor: '#FEF3C7', color: '#78350F' }}>
            <AlertTriangle size={10} className="inline mr-1" />
            An entry already exists for this outlet/date (₹{Number(impact.existing_amount || 0).toLocaleString('en-IN')}). Confirm will REPLACE it.
          </div>
        )}
        {p.notes && <p className="mt-2 text-[11px] italic" style={{ color: '#6B7280' }}>Note: {p.notes}</p>}
        {!readOnly && (
          <div className="flex gap-2 mt-3">
            <button onClick={onConfirm} className="flex-1 px-3 py-1.5 rounded text-xs font-medium text-white hover:opacity-90"
              style={{ backgroundColor: '#059669' }} data-testid="mira-confirm-btn">
              <CheckCircle2 size={11} className="inline mr-1" />{confirmLabel || 'Confirm'}
            </button>
            <button onClick={onModify} className="px-3 py-1.5 rounded text-xs font-medium border hover:bg-[#F7F5F2]"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }} data-testid="mira-modify-btn">
              <Edit3 size={11} className="inline mr-1" />Modify
            </button>
            <button onClick={onCancel} className="px-3 py-1.5 rounded text-xs font-medium border hover:bg-[#F7F5F2]"
              style={{ borderColor: '#E5E1DC', color: '#DC2626' }} data-testid="mira-cancel-btn">
              <X size={11} className="inline mr-1" />Cancel
            </button>
          </div>
        )}
        {readOnly && (
          <div className="flex gap-2 mt-3">
            <button onClick={onConfirm} className="flex-1 px-3 py-1.5 rounded text-xs font-medium text-white hover:opacity-90"
              style={{ backgroundColor: '#2A7B88' }}>Show me</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between">
      <span style={{ color: '#6B7280' }}>{label}:</span>
      <span className={highlight ? 'font-bold' : 'font-medium'} style={{ color: '#1B2A4A' }}>{value}</span>
    </div>
  );
}

// ─── Marketing Intelligence renderer ─────────────────────────────────────
// Handles all 7 Mira marketing intents (vips, lapsed, 30-day window, code
// scorecard, product affinity, SKU recommendation, weekly pulse). Each
// sub-type picks its own table layout; everything else is shared.
function MarketingResult({ msg }) {
  const intent = msg.intent;
  const data = msg.data || {};
  const INR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;

  // No-data gracefull fallback
  if (data.ok === false) {
    return (
      <div className="flex items-start gap-2" data-testid={`mkt-result-${intent}`}>
        <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
        <div className="flex-1 px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: '#FEF3C7', color: '#78350F', border: '1px solid #F59E0B' }}>
          {data.reason === 'unsupported_division'
            ? `Marketing data isn't available for your division yet — we currently have it for TDB only.`
            : (msg.humanSummary || 'No marketing data available.')}
        </div>
      </div>
    );
  }

  const renderVIPs = () => (
    <div>
      <p className="text-xs mb-2" style={{ color: '#6B7280' }}>
        Top {data.count || 0} VIPs · combined lifetime spend <span className="font-semibold" style={{ color: '#1B2A4A' }}>{INR(data.total_vip_spend)}</span>
      </p>
      <div className="space-y-1">
        {(data.items || []).map((v, i) => (
          <div key={v.email} className="grid grid-cols-12 items-center gap-2 px-2 py-1.5 rounded text-[12px]" style={{ backgroundColor: i % 2 ? '#FAFAF7' : 'white' }}>
            <span className="col-span-1 font-bold" style={{ color: '#2A7B88' }}>#{i + 1}</span>
            <span className="col-span-5 truncate" style={{ color: '#1B2A4A' }}>{v.email}</span>
            <span className="col-span-2 text-right font-bold" style={{ color: '#1B2A4A' }}>{INR(v.total_spend)}</span>
            <span className="col-span-2 text-right" style={{ color: '#6B7280' }}>{v.orders}x · {v.last_order_days_ago != null ? `${v.last_order_days_ago}d ago` : '—'}</span>
            <span className="col-span-2 truncate text-right text-[10px]" style={{ color: '#9CA3AF' }} title={v.top_sku}>{v.top_sku}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLapsed = () => (
    <div>
      <p className="text-xs mb-2" style={{ color: '#6B7280' }}>
        {data.count || 0} lapsed customers · silent <span className="font-semibold">{data.silent_days_threshold}+ days</span> · sorted by historical value
      </p>
      <div className="space-y-1">
        {(data.items || []).map((c, i) => (
          <div key={c.email} className="grid grid-cols-12 items-center gap-2 px-2 py-1.5 rounded text-[12px]" style={{ backgroundColor: i % 2 ? '#FAFAF7' : 'white' }}>
            <span className="col-span-5 truncate" style={{ color: '#1B2A4A' }}>{c.email}</span>
            <span className="col-span-2 text-right font-bold" style={{ color: '#1B2A4A' }}>{INR(c.total_spend)}</span>
            <span className="col-span-2 text-right" style={{ color: '#6B7280' }}>{c.orders}x</span>
            <span className="col-span-3 text-right font-semibold" style={{ color: c.silent_days > 180 ? '#D4726A' : '#BA7517' }}>{c.silent_days}d silent</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderWindow = () => (
    <div>
      <p className="text-xs mb-2" style={{ color: '#6B7280' }}>
        {data.count || 0} first-time buyers in the make-or-break window · nudge now before they cool off · total ₹ at risk <span className="font-semibold">{INR(data.total_spend_at_risk)}</span>
      </p>
      <div className="space-y-1">
        {(data.items || []).map((c, i) => (
          <div key={c.email} className="grid grid-cols-12 items-center gap-2 px-2 py-1.5 rounded text-[12px]" style={{ backgroundColor: i % 2 ? '#FAFAF7' : 'white' }}>
            <span className="col-span-5 truncate" style={{ color: '#1B2A4A' }}>{c.email}</span>
            <span className="col-span-2 text-right font-bold" style={{ color: '#1B2A4A' }}>{INR(c.total_spend)}</span>
            <span className="col-span-2 text-right" style={{ color: '#BA7517' }}>day {c.days_since_first}</span>
            <span className="col-span-3 truncate text-right text-[10px]" style={{ color: '#9CA3AF' }} title={c.first_sku}>{c.first_sku}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCodes = () => {
    const verdictColor = (v) => {
      if (v?.startsWith('excellent')) return { bg: '#D1FAE5', fg: '#065F46' };
      if (v?.startsWith('bleeding')) return { bg: '#FEE2E2', fg: '#991B1B' };
      if (v?.startsWith('ok')) return { bg: '#DBEAFE', fg: '#1E3A8A' };
      if (v?.startsWith('loyalty')) return { bg: '#F3E8FF', fg: '#6B21A8' };
      return { bg: '#F3F4F6', fg: '#374151' };
    };
    return (
      <div>
        <p className="text-xs mb-2" style={{ color: '#6B7280' }}>
          {(data.items || []).length} discount codes analysed · ranked by revenue
        </p>
        <div className="space-y-1">
          {(data.items || []).slice(0, 15).map((c) => {
            const col = verdictColor(c.verdict);
            return (
              <div key={c.code} className="px-2 py-1.5 rounded text-[12px] border" style={{ borderColor: '#F3F0EB' }}>
                <div className="flex items-center gap-2">
                  <span className="font-bold flex-1 truncate" style={{ color: '#1B2A4A' }}>{c.code}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase" style={{ backgroundColor: col.bg, color: col.fg }}>{c.verdict?.split(' — ')[0] || '—'}</span>
                </div>
                <div className="grid grid-cols-4 gap-1 mt-1 text-[11px]" style={{ color: '#6B7280' }}>
                  <span>{c.orders} orders</span>
                  <span>Rev {INR(c.revenue)}</span>
                  <span>Disc {INR(c.discount_given)}</span>
                  <span>{c.new_customers} new · {c.repeat_at_full_price_pct}% repeat</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAffinity = () => (
    <div>
      <p className="text-xs mb-2" style={{ color: '#6B7280' }}>
        {data.anchor ? `Products bought with "${data.anchor}"` : 'Top product pairs across all orders'}
      </p>
      <div className="space-y-1">
        {(data.items || []).slice(0, 15).map((p, i) => (
          <div key={i} className="px-2 py-1.5 rounded text-[12px] flex items-center gap-2" style={{ backgroundColor: i % 2 ? '#FAFAF7' : 'white' }}>
            {data.anchor ? (
              <>
                <span className="flex-1 truncate" style={{ color: '#1B2A4A' }}>{p.other_product}</span>
                <span className="font-bold text-right" style={{ color: '#2A7B88' }}>{p.co_orders} orders</span>
                <span className="text-right text-[10px]" style={{ color: '#9CA3AF' }}>attach {p.attach_rate_pct}%</span>
              </>
            ) : (
              <>
                <span className="flex-1 truncate" style={{ color: '#1B2A4A' }}>{p.a} + {p.b}</span>
                <span className="font-bold text-right" style={{ color: '#2A7B88' }}>{p.co_orders} co-orders</span>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderSKU = () => (
    <div className="grid md:grid-cols-2 gap-3 text-[12px]">
      <div>
        <p className="text-[10px] uppercase tracking-wide font-bold mb-1.5" style={{ color: '#065F46' }}>
          🚀 Gateway SKUs — push these
        </p>
        <div className="space-y-1">
          {(data.best_gateways || []).map((s, i) => (
            <div key={i} className="px-2 py-1.5 rounded flex items-center gap-2" style={{ backgroundColor: '#F0FDF4' }}>
              <span className="flex-1 truncate" style={{ color: '#1B2A4A' }}>{s.sku}</span>
              <span className="font-bold" style={{ color: '#065F46' }}>{s.repeat_pct}%</span>
              <span className="text-[10px]" style={{ color: '#6B7280' }}>({s.first_buyers})</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide font-bold mb-1.5" style={{ color: '#991B1B' }}>
          🔻 Problem SKUs — burn new buyers
        </p>
        <div className="space-y-1">
          {(data.problem_skus || []).map((s, i) => (
            <div key={i} className="px-2 py-1.5 rounded flex items-center gap-2" style={{ backgroundColor: '#FEF2F2' }}>
              <span className="flex-1 truncate" style={{ color: '#1B2A4A' }}>{s.sku}</span>
              <span className="font-bold" style={{ color: '#991B1B' }}>{s.repeat_pct}%</span>
              <span className="text-[10px]" style={{ color: '#6B7280' }}>({s.first_buyers})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPulse = () => {
    const w = data.week || {};
    const prev = data.prev_week || {};
    const revColor = (w.revenue_wow_pct || 0) >= 0 ? '#065F46' : '#991B1B';
    return (
      <div className="space-y-3 text-[12px]">
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 rounded" style={{ backgroundColor: '#F7F5F2' }}>
            <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B7280' }}>Revenue</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{INR(w.revenue)}</p>
            <p className="text-[10px] font-semibold" style={{ color: revColor }}>{w.revenue_wow_pct >= 0 ? '↑' : '↓'} {Math.abs(w.revenue_wow_pct || 0)}% WoW</p>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: '#F7F5F2' }}>
            <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B7280' }}>Orders</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{w.orders || 0}</p>
            <p className="text-[10px]" style={{ color: '#6B7280' }}>prev week: {prev.orders || 0}</p>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: '#F7F5F2' }}>
            <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B7280' }}>Customers</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{w.customers || 0}</p>
            <p className="text-[10px]" style={{ color: '#6B7280' }}>prev week: {prev.customers || 0}</p>
          </div>
        </div>
        {(data.trending_skus || []).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide font-bold mb-1" style={{ color: '#2A7B88' }}>Trending this week</p>
            <div className="flex flex-wrap gap-1">
              {(data.trending_skus || []).map((s, i) => (
                <span key={i} className="px-2 py-1 rounded text-[11px]" style={{ backgroundColor: '#E0F2FE', color: '#1B2A4A' }}>{s.sku} · {s.units}u</span>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 rounded" style={{ backgroundColor: '#FFFBEB' }}>
            <p className="text-[10px] uppercase tracking-wide font-bold" style={{ color: '#78350F' }}>Who to WhatsApp</p>
            <p className="text-[11px] mt-1" style={{ color: '#78350F' }}>
              {data.lapsed_total_count || 0} lapsed VIPs<br />
              {data.window_total_count || 0} first-time buyers at risk
            </p>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: '#FEF2F2' }}>
            <p className="text-[10px] uppercase tracking-wide font-bold" style={{ color: '#991B1B' }}>Bleeding codes</p>
            <p className="text-[11px] mt-1" style={{ color: '#991B1B' }}>
              {(data.bleeding_codes || []).length === 0 ? 'None 🙌' :
                (data.bleeding_codes || []).slice(0, 3).map((c) => c.code).join(', ')}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderDrafts = () => {
    const copyMsg = (txt) => {
      try {
        navigator.clipboard.writeText(txt);
        toast.success('Copied to clipboard');
      } catch { toast.error('Copy failed — select + copy manually'); }
    };
    const waLink = (phone, message) => {
      const p = (phone || '').replace(/[^\d+]/g, '');
      const q = encodeURIComponent(message || '');
      if (!p) return null;
      return `https://wa.me/${p.replace(/^\+/, '')}?text=${q}`;
    };
    return (
      <div>
        <p className="text-xs mb-2" style={{ color: '#6B7280' }}>
          {data.count || 0} {data.audience || 'customer'} messages drafted — tap WhatsApp to open the chat with the draft pre-filled.
          {data.discount_code_used && <> Using code <span className="font-semibold">{data.discount_code_used}</span>.</>}
        </p>
        <div className="space-y-2">
          {(data.items || []).map((c, i) => {
            const link = waLink(c.phone, c.draft_message);
            return (
              <div key={c.email} className="rounded border p-2" style={{ borderColor: '#E5E1DC', backgroundColor: i % 2 ? '#FAFAF7' : 'white' }} data-testid={`draft-msg-${i}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-bold" style={{ color: '#1B2A4A' }}>{c.first_name || '—'}</span>
                  <span className="text-[11px]" style={{ color: '#6B7280' }}>· {c.email}</span>
                  {c.phone ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>📱 {c.phone}</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>no phone</span>
                  )}
                  {c.silent_days ? (
                    <span className="text-[10px] font-semibold ml-auto" style={{ color: '#BA7517' }}>{c.silent_days}d silent</span>
                  ) : c.days_since_first ? (
                    <span className="text-[10px] font-semibold ml-auto" style={{ color: '#BA7517' }}>day {c.days_since_first}</span>
                  ) : null}
                </div>
                <p className="text-[12px] whitespace-pre-wrap mb-2 p-2 rounded" style={{ color: '#1B2A4A', backgroundColor: 'white', borderLeft: '2px solid #2A7B88' }}>
                  {c.draft_message}
                </p>
                <div className="flex gap-1.5">
                  {link && (
                    <a href={link} target="_blank" rel="noreferrer" data-testid={`wa-send-${i}`}
                       className="text-[11px] px-2.5 py-1 rounded font-semibold text-white hover:opacity-90 inline-flex items-center gap-1"
                       style={{ backgroundColor: '#25D366' }}>
                      💬 Open WhatsApp
                    </a>
                  )}
                  <button onClick={() => copyMsg(c.draft_message)} data-testid={`copy-msg-${i}`}
                          className="text-[11px] px-2.5 py-1 rounded font-semibold border hover:bg-[#F7F5F2]"
                          style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}>
                    Copy
                  </button>
                  {c.total_spend ? (
                    <span className="ml-auto text-[10px] self-center" style={{ color: '#6B7280' }}>
                      LTV ₹{Math.round(c.total_spend).toLocaleString('en-IN')} · {c.orders}x
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCompare = () => {
    const pa = data.period_a || {}, pb = data.period_b || {};
    const delta = data.delta || {};
    const pr = data.projection;
    const sortedCities = (data.by_city || [])
      .filter(c => c.revenue_a > 0 || c.revenue_b > 0)
      .sort((a, b) => (b.revenue_a + b.revenue_b) - (a.revenue_a + a.revenue_b));
    const deltaColor = (n) => n == null ? '#6B7280' : n >= 0 ? '#065F46' : '#991B1B';
    const deltaLabel = (n) => n == null ? '—' : `${n >= 0 ? '↑' : '↓'} ${Math.abs(n)}%`;
    return (
      <div className="space-y-3 text-[12px]">
        {data.interpretation && (
          <div className="p-3 rounded border-l-4" style={{ backgroundColor: '#F0FDF4', borderColor: '#2D8B5E' }}>
            <p className="text-[12px] font-medium leading-relaxed" style={{ color: '#065F46' }}>{data.interpretation}</p>
          </div>
        )}
        {(data.warnings || []).length > 0 && (
          <div className="p-2 rounded" style={{ backgroundColor: '#FEF3C7' }}>
            {data.warnings.map((w, i) => (
              <p key={i} className="text-[11px]" style={{ color: '#78350F' }}>⚠ {w}</p>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 rounded border" style={{ backgroundColor: '#F7F5F2', borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#6B7280' }}>{pa.label}</p>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{pa.start} → {pa.end} ({pa.days} days)</p>
            <p className="text-xl font-bold mt-1" style={{ color: '#1B2A4A' }}>{INR(pa.revenue)}</p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>{pa.orders} orders · ₹{Math.round(pa.daily_avg).toLocaleString('en-IN')}/day</p>
          </div>
          <div className="p-3 rounded border" style={{ backgroundColor: '#ECFDF5', borderColor: '#2D8B5E' }}>
            <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#065F46' }}>{pb.label}</p>
            <p className="text-[10px]" style={{ color: '#047857' }}>{pb.start} → {pb.end} ({pb.days} days)</p>
            <p className="text-xl font-bold mt-1" style={{ color: '#1B2A4A' }}>{INR(pb.revenue)}</p>
            <p className="text-[11px]" style={{ color: '#065F46' }}>{pb.orders} orders · ₹{Math.round(pb.daily_avg).toLocaleString('en-IN')}/day</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 rounded" style={{ backgroundColor: '#FAFAF7' }}>
            <p className="text-[10px] uppercase" style={{ color: '#6B7280' }}>Revenue YoY</p>
            <p className="text-sm font-bold" style={{ color: deltaColor(delta.revenue_pct) }}>{deltaLabel(delta.revenue_pct)}</p>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: '#FAFAF7' }}>
            <p className="text-[10px] uppercase" style={{ color: '#6B7280' }}>Orders YoY</p>
            <p className="text-sm font-bold" style={{ color: deltaColor(delta.orders_pct) }}>{deltaLabel(delta.orders_pct)}</p>
          </div>
          <div className="p-2 rounded" style={{ backgroundColor: '#FAFAF7' }}>
            <p className="text-[10px] uppercase" style={{ color: '#6B7280' }}>Daily run-rate YoY</p>
            <p className="text-sm font-bold" style={{ color: deltaColor(delta.daily_avg_pct) }}>{deltaLabel(delta.daily_avg_pct)}</p>
          </div>
        </div>
        {pr && (
          <div className="p-2 rounded" style={{ backgroundColor: '#FFFBEB', borderLeft: '3px solid #BA7517' }}>
            <p className="text-[11px]" style={{ color: '#78350F' }}>
              <span className="font-semibold">At current pace</span>, {pb.label} will finish at{' '}
              <span className="font-bold">{INR(pr.projected_revenue)}</span> by {pr.project_to}
              {' '}<span style={{ color: deltaColor(pr.delta_vs_a_pct) }}>({deltaLabel(pr.delta_vs_a_pct)} vs {pa.label})</span>
            </p>
          </div>
        )}
        {sortedCities.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: '#6B7280' }}>By city</p>
            <div className="space-y-1">
              {sortedCities.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-2 py-1.5 rounded text-[11px]" style={{ backgroundColor: i % 2 ? '#FAFAF7' : 'white' }}>
                  <span className="col-span-3 font-semibold" style={{ color: '#1B2A4A' }}>{c.city}</span>
                  <span className="col-span-3 text-right" style={{ color: '#6B7280' }}>{INR(c.revenue_a)}</span>
                  <span className="col-span-3 text-right font-bold" style={{ color: '#1B2A4A' }}>{INR(c.revenue_b)}</span>
                  <span className="col-span-3 text-right font-bold" style={{ color: deltaColor(c.delta_pct) }}>{deltaLabel(c.delta_pct)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSalesSummary = () => {
    const byCity = Object.entries(data.by_city || {});
    const byChannel = Object.entries(data.by_channel || {});
    const total = data.revenue || 0;
    return (
      <div className="space-y-3 text-[12px]">
        <div className="p-3 rounded-lg border-2" style={{ borderColor: '#2A7B88', backgroundColor: '#ECFDF5' }}>
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: '#065F46' }}>{data.label}</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#1B2A4A' }}>{INR(total)}</p>
          <p className="text-[11px]" style={{ color: '#065F46' }}>
            {data.data_days} days of data · ₹{Math.round(data.daily_avg).toLocaleString('en-IN')}/day
            {data.source === 'daily_sales' ? ' · outlet POS' : ' · website orders'}
          </p>
          {data.projection && (
            <p className="text-[11px] font-semibold mt-1" style={{ color: '#065F46' }}>
              At this pace → {INR(data.projection.projected_revenue)} by {data.projection.project_to}
            </p>
          )}
        </div>

        {byCity.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: '#6B7280' }}>By outlet</p>
            <div className="space-y-1">
              {byCity.map(([c, v]) => (
                <div key={c} className="grid grid-cols-12 gap-2 px-2 py-1.5 rounded text-[11px]" style={{ backgroundColor: '#FAFAF7' }}>
                  <span className="col-span-4 font-semibold" style={{ color: '#1B2A4A' }}>{c}</span>
                  <span className="col-span-4 text-right font-bold" style={{ color: '#1B2A4A' }}>{INR(v)}</span>
                  <span className="col-span-4 text-right" style={{ color: '#6B7280' }}>{total > 0 ? `${Math.round(v / total * 100)}% of total` : '—'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {byChannel.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: '#6B7280' }}>By channel</p>
            <div className="flex flex-wrap gap-1">
              {byChannel.slice(0, 6).map(([ch, v]) => (
                <span key={ch} className="px-2 py-1 rounded text-[11px]" style={{ backgroundColor: '#E0F2FE', color: '#1B2A4A' }}>
                  {ch}: {INR(v)} ({total > 0 ? Math.round(v / total * 100) : 0}%)
                </span>
              ))}
            </div>
          </div>
        )}

        {(data.best_day?.date || data.worst_day?.date) && (
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            {data.best_day?.date && (
              <div className="p-2 rounded" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
                🔥 Best: {data.best_day.date} · {INR(data.best_day.amount)}
              </div>
            )}
            {data.worst_day?.date && (
              <div className="p-2 rounded" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                ❄️ Worst: {data.worst_day.date} · {INR(data.worst_day.amount)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const body = (() => {
    switch (intent) {
      case 'marketing_vips': return renderVIPs();
      case 'marketing_lapsed': return renderLapsed();
      case 'marketing_30day_window': return renderWindow();
      case 'marketing_code_scorecard': return renderCodes();
      case 'marketing_affinity': return renderAffinity();
      case 'marketing_sku_recommendation': return renderSKU();
      case 'marketing_pulse': return renderPulse();
      case 'marketing_draft_messages': return renderDrafts();
      case 'marketing_compare_periods': return renderCompare();
      case 'marketing_sales_summary': return renderSalesSummary();
      default: return <pre className="text-[11px] overflow-auto">{JSON.stringify(data, null, 2)}</pre>;
    }
  })();

  return (
    <div className="flex items-start gap-2" data-testid={`mkt-result-${intent}`}>
      <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
      <div className="flex-1 rounded-lg border p-3" style={{ borderColor: '#2A7B88', backgroundColor: 'white', borderLeftWidth: 3 }}>
        <p className="text-[11px] uppercase tracking-wide font-medium mb-2" style={{ color: '#2A7B88' }}>
          {intent.replace('marketing_', '').replace('_', ' ')}
        </p>
        {msg.humanSummary && (
          <p className="text-sm font-medium mb-3" style={{ color: '#1B2A4A' }}>{msg.humanSummary}</p>
        )}
        {body}
      </div>
    </div>
  );
}

function _siteLinkFor(entityType, parsed = {}) {
  // Map each Mira write to the best place on the site to "see it landed".
  // Returns {href, label} or null if no obvious destination exists yet.
  const div = parsed.division_id;
  const outletSlug = parsed.outlet && encodeURIComponent(parsed.outlet);
  switch (entityType) {
    case 'daily_sales':
      return div
        ? { href: `/division/${div}`, label: `View ${div.toUpperCase()} dashboard` }
        : null;
    case 'b2b_revenue':
    case 'pipeline_deals':
    case 'contracts':
      return { href: '/bookings', label: 'View pipeline' };
    case 'work_orders':
    case 'assets':
    case 'compliance_items':
      if (div && outletSlug) return { href: `/division/${div}/outlet/${outletSlug}`, label: `View ${parsed.outlet}` };
      if (div) return { href: `/division/${div}`, label: `View ${div.toUpperCase()} dashboard` };
      return null;
    case 'recipes':
    case 'tdb_cost_entries':
      return { href: '/tdb/cost', label: 'View TDB cost module' };
    case 'people':
      return parsed.person_id
        ? { href: `/person/${parsed.person_id}`, label: 'View person' }
        : (div ? { href: `/division/${div}`, label: 'View people in division' } : null);
    case 'sops':
    case 'sop_translations':
      return { href: '/my-training', label: 'View training' };
    case 'vendors':
      return { href: '/admin?tab=vendors', label: 'View vendors' };
    case 'meeting_notes':
      return { href: '/bookings', label: 'View client notes' };
    default:
      return div ? { href: `/division/${div}`, label: 'View division' } : null;
  }
}

function Receipt({ msg, onUndo }) {
  const { receipt, action, undoExpiresAt, entityType, parsed = {} } = msg;
  const [undone, setUndone] = useState(false);
  const link = _siteLinkFor(entityType, parsed);
  return (
    <div className="flex items-start gap-2" data-testid="mira-receipt">
      <CheckCircle2 size={14} style={{ color: '#059669' }} className="flex-shrink-0 mt-1" />
      <div className="flex-1 rounded-lg border p-3" style={{ borderColor: '#059669', backgroundColor: '#ECFDF5', borderLeftWidth: 3 }}>
        <p className="text-sm font-semibold mb-1" style={{ color: '#065F46' }}>
          {action === 'create' ? '✅ Saved' : action === 'update' ? '↻ Updated' : '✅ Done'}
        </p>
        <p className="text-xs mb-2" style={{ color: '#047857' }}>{receipt?.human_summary}</p>
        {parsed.impact_note && (
          <p className="text-[11.5px] mb-2" style={{ color: '#065F46' }}>↻ {parsed.impact_note}</p>
        )}
        {(receipt?.before || receipt?.after) && (
          <div className="text-[11px] mb-2 p-2 rounded bg-white">
            <p className="text-[10px] font-medium uppercase mb-0.5" style={{ color: '#6B7280' }}>Before → After</p>
            {receipt.before && receipt.before.amount !== undefined && (
              <p style={{ color: '#991B1B' }}>Before: ₹{Number(receipt.before.amount || 0).toLocaleString('en-IN')}</p>
            )}
            {receipt.after && receipt.after.amount !== undefined && (
              <p style={{ color: '#065F46' }}>After: ₹{Number(receipt.after.amount || 0).toLocaleString('en-IN')}</p>
            )}
            {!receipt.before && <p style={{ color: '#9CA3AF' }}>New record created.</p>}
          </div>
        )}
        {/* Strategic analysis — Claude's 3-paragraph response */}
        {msg.analysis && (
          <div className="text-[12px] leading-relaxed mb-2 p-2.5 rounded bg-white whitespace-pre-wrap"
               style={{ color: '#1B2A4A' }} data-testid="mira-analysis-body">
            {msg.analysis}
          </div>
        )}
        {/* Web research results */}
        {msg.research && (msg.research.answer || msg.research.results?.length) && (
          <div className="text-[12px] mb-2 p-2.5 rounded bg-white" data-testid="mira-research-body">
            {msg.research.answer && (
              <p className="mb-2" style={{ color: '#1B2A4A' }}>{msg.research.answer}</p>
            )}
            {(msg.research.results || []).slice(0, 5).map((r, i) => (
              <div key={i} className="mb-1.5">
                <a href={r.url} target="_blank" rel="noopener noreferrer"
                   className="text-[11px] font-semibold hover:underline" style={{ color: '#2A7B88' }}>
                  {r.title}
                </a>
                <p className="text-[10px]" style={{ color: '#6B7280' }}>{r.snippet}</p>
              </div>
            ))}
          </div>
        )}
        {/* Drafted message — show body + Send + Copy */}
        {msg.draft?.body && (
          <div className="text-[12px] mb-2 p-2.5 rounded bg-white"
               style={{ border: '1px dashed #2A7B88' }}
               data-testid="mira-draft-body">
            {msg.draft.subject && (
              <p className="text-[11px] font-semibold" style={{ color: '#2A7B88' }}>
                Subject: {msg.draft.subject}
              </p>
            )}
            <pre className="text-[12px] whitespace-pre-wrap font-sans mt-1" style={{ color: '#1B2A4A' }}>
              {msg.draft.body}
            </pre>
            <div className="mt-2 flex gap-2">
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mira-exec/send-message`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('scaleboard_token') || ''}`,
                      },
                      body: JSON.stringify(msg.draft),
                    });
                    const data = await res.json();
                    if (data.ok) alert(`✓ ${msg.draft.channel} sent!`);
                    else alert(`✗ Failed: ${data.receipt?.human_summary || 'unknown'}`);
                  } catch (e) { alert('Send failed: ' + e.message); }
                }}
                className="text-[11px] px-2.5 py-1 rounded-md text-white"
                style={{ backgroundColor: '#2A7B88' }}
                data-testid="mira-send-draft-btn">
                Send {msg.draft.channel}
              </button>
              <button
                onClick={() => { navigator.clipboard.writeText(msg.draft.body); }}
                className="text-[11px] px-2.5 py-1 rounded-md border"
                style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
                Copy
              </button>
            </div>
          </div>
        )}
        {/* Generated file download */}
        {msg.file?.download_url && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}${msg.file.download_url}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md mb-2 hover:opacity-90"
            style={{ backgroundColor: '#2A7B88', color: 'white' }}
            data-testid="mira-download-btn"
          >
            ⬇ Download {msg.file.kind?.toUpperCase()} · {msg.file.title}
            {msg.file.size_kb ? ` · ${msg.file.size_kb} KB` : ''}
          </a>
        )}
        <div className="flex items-center flex-wrap gap-3">
          {link && (
            <a
              href={link.href}
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-md text-white hover:opacity-90"
              style={{ backgroundColor: '#059669' }}
              data-testid="mira-view-on-site-btn"
            >
              <ExternalLink size={11} />
              {link.label}
            </a>
          )}
          {!undone && (
            <button
              onClick={() => { onUndo(msg); setUndone(true); }}
              className="text-[11px] underline"
              style={{ color: '#065F46' }}
              data-testid="mira-undo-btn"
            >
              <RotateCcw size={10} className="inline mr-1" />
              Undo (valid until {new Date(undoExpiresAt).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })})
            </button>
          )}
          {undone && (
            <span className="text-[11px] italic" style={{ color: '#9CA3AF' }}>Reverted</span>
          )}
        </div>
      </div>
    </div>
  );
}



function ReconcileReport({ msg }) {
  const { matches = [], mismatches = [], missing = [], collection, totalMissing } = msg;
  const dot = (color) => <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: color }} />;
  return (
    <div className="flex items-start gap-2" data-testid="mira-reconcile-report">
      <Bot size={14} style={{ color: '#2A7B88' }} className="flex-shrink-0 mt-1" />
      <div className="flex-1 rounded-lg border p-3 text-xs" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
        <p className="font-semibold text-sm mb-2" style={{ color: '#1B2A4A' }}>
          {msg.humanSummary || `Reconciled against ${collection}`}
        </p>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="rounded-md p-2" style={{ backgroundColor: '#ECFDF5', border: '1px solid #D1FAE5' }}>
            <p className="text-[10px] uppercase" style={{ color: '#065F46' }}>{dot('#2D8B5E')}Match</p>
            <p className="text-lg font-mono font-bold" style={{ color: '#065F46' }}>{matches.length}</p>
          </div>
          <div className="rounded-md p-2" style={{ backgroundColor: '#FEF3E5', border: '1px solid #FED7AA' }}>
            <p className="text-[10px] uppercase" style={{ color: '#9A3412' }}>{dot('#C98A1F')}Differ</p>
            <p className="text-lg font-mono font-bold" style={{ color: '#9A3412' }}>{mismatches.length}</p>
          </div>
          <div className="rounded-md p-2" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA' }}>
            <p className="text-[10px] uppercase" style={{ color: '#991B1B' }}>{dot('#B91C1C')}Missing</p>
            <p className="text-lg font-mono font-bold" style={{ color: '#991B1B' }}>{missing.length}</p>
          </div>
        </div>
        {missing.length > 0 && (
          <div className="mb-2">
            <p className="text-[11px] font-semibold mb-1" style={{ color: '#991B1B' }}>
              Missing rows{totalMissing ? ` · ₹${Number(totalMissing).toLocaleString('en-IN')}` : ''}:
            </p>
            <div className="space-y-0.5 max-h-40 overflow-y-auto font-mono text-[11px]">
              {missing.slice(0, 10).map((r, i) => (
                <div key={i} className="flex justify-between border-b pb-0.5" style={{ borderColor: '#F3F0EB' }}>
                  <span className="truncate">{r.date || r.invoice_date || '—'} · {r.outlet || r.vendor_name || r.client_name || '—'}</span>
                  <span>₹{Number(r.amount || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
              {missing.length > 10 && (
                <p className="italic pt-1" style={{ color: '#9CA3AF' }}>… and {missing.length - 10} more</p>
              )}
            </div>
          </div>
        )}
        {mismatches.length > 0 && (
          <div className="mb-2">
            <p className="text-[11px] font-semibold mb-1" style={{ color: '#9A3412' }}>Different amounts:</p>
            <div className="space-y-0.5 max-h-32 overflow-y-auto font-mono text-[11px]">
              {mismatches.slice(0, 6).map((r, i) => (
                <div key={i} className="flex justify-between gap-2 border-b pb-0.5" style={{ borderColor: '#F3F0EB' }}>
                  <span className="truncate">{r.date || r.invoice_date || '—'} · {r.outlet || r.vendor_name || '—'}</span>
                  <span>DB: ₹{Number(r.db_amount || 0).toLocaleString('en-IN')} · Paste: ₹{Number(r.pasted_amount || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {matches.length > 0 && missing.length === 0 && mismatches.length === 0 && (
          <p className="italic text-[11px]" style={{ color: '#065F46' }}>
            ✓ Every row is already in the system — you're fully up to date.
          </p>
        )}
      </div>
    </div>
  );
}
